/**
 * CR325: Ribbon View Model
 * 
 * Unified helper for deriving ribbon display data from deal + config.
 * Used by both RibbonComposer (admin preview) and RibbonBlock (v2 renderer).
 * 
 * Single source of truth for:
 * - Amount derivation per deal type
 * - Label resolution (default vs custom)
 * - Merchant subline resolution
 */

import type { Deal, DealCategory } from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";
import { getDealRibbonLabel } from "@/lib/deals/dealTypeRegistry";
import { resolveBonusValueModel, formatCurrency } from "./bonusValueModel";

// Number formatter for points (no decimals) - "15,000" format
const POINTS_FORMATTER = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 });

// ================================================================
// TYPES
// ================================================================

/**
 * Ribbon configuration from template_json.components.ribbon
 */
export interface RibbonComponentConfig {
  primary?: {
    showDealTypeLabel?: boolean;
    labelMode?: "default" | "custom";
    labelOverride?: string | null;
    showAmount?: boolean;
    separator?: "space" | "bullet" | "dash";
    amountMode?: "reward_amount" | "percent_off" | "points" | "computed_value";
    // CR335: Rate qualifier control for POINTS deals ("per $X")
    rateQualifier?: "auto" | "hide" | "compact";
  };
  merchantSubline?: {
    enabled?: boolean;
    showLogo?: boolean;
    showName?: boolean;
  };
  typography?: {
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: number;
  };
  merchantTypography?: {
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: number;
  } | null;
}

/**
 * Resolved view model for ribbon rendering
 */
export interface RibbonViewModel {
  /** Full rendered ribbon text (e.g., "BONUS $20") */
  primaryText: string;
  /** Deal type label portion (e.g., "BONUS") */
  dealTypeLabel: string | null;
  /** Amount portion raw value */
  amountRaw: number | null;
  /** Amount portion formatted (e.g., "$20", "15%", "15,000" for POINTS) */
  amountFormatted: string | null;
  /** CR335: Rate qualifier text (e.g., "per $100", "/$100") - for POINTS */
  rateQualifierText: string | null;
  /** Separator between label and amount */
  separator: string;
  /** Whether merchant subline should show */
  showSubline: boolean;
  /** Merchant name for subline */
  merchantName: string | null;
  /** Merchant logo URL for subline (if available) */
  merchantLogoUrl: string | null;
  /** Typography config */
  typography: RibbonComponentConfig["typography"];
  /** Merchant typography (null = inherit from ribbon) */
  merchantTypography: RibbonComponentConfig["merchantTypography"];
  /** Deal category for theming */
  dealCategory: DealCategory;
}

// ================================================================
// SEPARATOR MAP
// ================================================================

const SEPARATOR_DISPLAY: Record<string, string> = {
  space: " ",
  bullet: " • ",
  dash: " — ",
};

// ================================================================
// AMOUNT DERIVATION
// ================================================================

/**
 * Derive the amount value for ribbon display based on deal type and amountMode.
 * Returns both raw number and formatted string.
 */
export function deriveRibbonAmountForDeal(
  deal: Deal | Record<string, unknown>,
  dealCategory: DealCategory,
  amountMode?: string
): { raw: number | null; formatted: string | null } {
  // Cast to Deal for type checking (functions handle partial data gracefully)
  const typedDeal = deal as unknown as Deal;
  const rawDeal = deal as Record<string, unknown>;
  
  // Determine effective mode based on deal type
  const effectiveMode = amountMode ?? getDefaultAmountMode(dealCategory);
  
  switch (effectiveMode) {
    case "reward_amount": {
      // BONUS: Show bonus/reward amount
      const valueModel = resolveBonusValueModel(typedDeal);
      if (valueModel.rewardAmount != null) {
        return {
          raw: valueModel.rewardAmount,
          formatted: formatCurrency(valueModel.rewardAmount),
        };
      }
      // Fallback to various bonus amount fields
      const bonusAmount = (rawDeal.bonus_amount ?? rawDeal.bonus_or_discount_amount) as number | undefined;
      if (bonusAmount != null) {
        return { raw: bonusAmount, formatted: formatCurrency(bonusAmount) };
      }
      return { raw: null, formatted: null };
    }
    
    case "percent_off": {
      // DISCOUNT: Show discount percentage
      const discountPercent = rawDeal.discount_percent as number | undefined;
      if (discountPercent != null) {
        return { raw: discountPercent, formatted: `${discountPercent}%` };
      }
      return { raw: null, formatted: null };
    }
    
    case "points": {
      // POINTS: Show points amount as numeric only (no "pts" suffix)
      // The label/unit comes from Deal Type Label config
      const pointsAmount = rawDeal.points_amount as number | undefined;
      if (pointsAmount != null && pointsAmount > 0) {
        return {
          raw: pointsAmount,
          formatted: POINTS_FORMATTER.format(pointsAmount), // "15,000" - no suffix
        };
      }
      return { raw: null, formatted: null };
    }
    
    case "computed_value":
    default: {
      // Generic: Use computed value
      const computedValue = (rawDeal.computed_value_cad ?? 
        (rawDeal.canonical_deal_json as any)?.value?.computed_value_cad) as number | undefined;
      if (computedValue != null) {
        return { raw: computedValue, formatted: formatCurrency(computedValue) };
      }
      return { raw: null, formatted: null };
    }
  }
}

/**
 * Get the default amountMode for a deal category.
 */
function getDefaultAmountMode(dealCategory: DealCategory): string {
  switch (dealCategory) {
    case "BONUS": return "reward_amount";
    case "DISCOUNT": return "percent_off";
    case "POINTS": return "points";
    default: return "computed_value";
  }
}

// ================================================================
// VIEW MODEL BUILDER
// ================================================================

/**
 * Build the complete ribbon view model from deal + config.
 * 
 * This is the single source of truth - used by both:
 * - RibbonComposer preview (admin UI)
 * - RibbonBlock render (v2 card)
 * 
 * CR326: All deal types (including POINTS) use the same config-driven logic:
 * - Amount toggle controls numeric display
 * - Deal Type Label toggle controls label (default or custom)
 * - Composition: "{amount} {label}" based on enabled toggles
 */
export function buildRibbonViewModel(
  deal: Deal | Record<string, unknown> | null | undefined,
  dealTypeKey: string,
  config?: RibbonComponentConfig | null
): RibbonViewModel {
  const rawDeal = (deal ?? {}) as Record<string, unknown>;
  const typedDeal = deal as unknown as Deal | undefined;
  
  // Determine deal category
  const { category: dealCategory } = typedDeal 
    ? getDealType(typedDeal) 
    : { category: dealTypeKey.toUpperCase() as DealCategory };
  
  // Extract config values with defaults
  const primary = config?.primary ?? {};
  const subline = config?.merchantSubline ?? {};
  const showDealTypeLabel = primary.showDealTypeLabel ?? true;
  const labelMode = primary.labelMode ?? "default";
  const labelOverride = primary.labelOverride;
  const showAmount = primary.showAmount ?? true;
  const separatorKey = primary.separator ?? "space";
  const amountMode = primary.amountMode;
  
  // Resolve deal type label
  let dealTypeLabel: string | null = null;
  if (showDealTypeLabel) {
    if (labelMode === "custom" && labelOverride) {
      dealTypeLabel = labelOverride;
    } else {
      dealTypeLabel = getDealRibbonLabel(dealCategory) ?? dealCategory;
    }
  }
  
  // Resolve amount
  const { raw: amountRaw, formatted: amountFormatted } = showAmount
    ? deriveRibbonAmountForDeal(rawDeal as unknown as Deal, dealCategory, amountMode)
    : { raw: null, formatted: null };
  
  // Build separator
  const separator = SEPARATOR_DISPLAY[separatorKey] ?? " ";
  
  // Build primary text - compose from enabled parts
  // CR326: If both disabled, return empty string (blank ribbon)
  const parts: string[] = [];
  if (amountFormatted) parts.push(amountFormatted);
  if (dealTypeLabel) parts.push(dealTypeLabel);
  const primaryText = parts.join(separator);
  
  // Resolve merchant subline
  const showSubline = subline.enabled ?? false;
  const merchantName = showSubline
    ? (rawDeal.promo_merchant_name ?? rawDeal.merchant) as string | null
    : null;
  
  // Logo URL would come from asset pipeline - null for now
  const merchantLogoUrl: string | null = null;
  
  return {
    primaryText,
    dealTypeLabel,
    amountRaw,
    amountFormatted,
    rateQualifierText: null,
    separator,
    showSubline,
    merchantName,
    merchantLogoUrl,
    typography: config?.typography,
    merchantTypography: config?.merchantTypography,
    dealCategory,
  };
}

/**
 * Legacy helper for backward compatibility with RibbonComposer.
 * @deprecated Use buildRibbonViewModel instead
 */
export function deriveRibbonAmount(deal: Record<string, unknown> | null | undefined): string | null {
  if (!deal) return null;
  const typedDeal = deal as unknown as Deal;
  const { category: dealCategory } = getDealType(typedDeal);
  const { formatted } = deriveRibbonAmountForDeal(typedDeal, dealCategory);
  return formatted;
}

/**
 * Legacy helper for backward compatibility with RibbonComposer.
 * @deprecated Use buildRibbonViewModel instead
 */
export function getMerchantSublineName(deal: Deal | Record<string, unknown> | null | undefined): string | null {
  if (!deal) return null;
  return (deal.promo_merchant_name ?? deal.merchant) as string | null;
}
